import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { makeStyles } from '@material-ui/core/styles';

import {
  Criterium,
  CriteriumTable,
  CriteriumSnippetElement,
  CriteriumSkeleton
} from '@hydra/common/criterium';
import { Translate } from '@hydra/common/components';
import { useReviewContext } from '@hydra/common/context';

const MAX_AMOUNT_TO_SHOW = 10;

const useStyles = makeStyles(theme => ({
  sample: {
    textAlign: 'center',
    width: theme.spacing(7),
    height: theme.spacing(7),
    fontWeight: theme.typography.fontWeightSemibold,
    fontSize: theme.typography.fontSizeLarge,
    margin: 'auto',
    display: 'table-cell',
    verticalAlign: 'middle'
  },
  contrastRatio: {
    textAlign: 'center'
  }
}));

export const Sample = ({ bgColor, fgColor }) => {
  const classes = useStyles();
  return (
    <div className={classes.sample} style={{ backgroundColor: bgColor, color: fgColor }}>Aa</div>
  );
};

export const ContrastRatio = ({ contrastRatio, expectedContrastRatio }) => {
  const classes = useStyles();
  return (
    <div className={classnames(classes.contrastRatio, classes.webkitBox)}>
      {contrastRatio}
      <Translate
        component='div'
        content='accessibility_contrast_expected_ratio'
        with={{ expectedContrastRatio }}
      />
    </div>
  );
};

export const AccessibilityContrast = ({ criterium }) => {
  const { data } = criterium;
  const { nodes } = data;
  const { variant } = useReviewContext();

  const head = [
    <Translate content='accessibility_contrast_element' key='element' />,
    <Translate content='accessibility_contrast_ratio' key='ratio' />,
    <Translate content='accessibility_contrast_sample' key='sample' />
  ];

  const rows = nodes
    .slice(0, MAX_AMOUNT_TO_SHOW)
    .map(({ snippet, nodeLabel, fgColor, bgColor, contrastRatio, expectedContrastRatio }, i) => {
      return [
        <CriteriumSnippetElement key={`element-${i}`} snippet={snippet} nodeLabel={nodeLabel} />,
        <ContrastRatio key={`ratio-${i}`} contrastRatio={`${contrastRatio}:1`} expectedContrastRatio={`${expectedContrastRatio}:1`} />,
        <Sample key={`sample-${i}`} fgColor={fgColor} bgColor={bgColor} />
      ];
    });

  const tableClasses = {
    head: ['', 'center', ''],
    body: ['verticalAlignMiddle', 'verticalAlignMiddle', 'verticalAlignMiddle']
  };
  const content = (
    <>
      {
        nodes.length > 0
          ? (
            <div>
              {variant === 'nocrit' ? <CriteriumSkeleton width='60%' /> : <Translate content='accessibility_contrast_summary' />}
              <CriteriumTable
                fixedColumns={[60, 25, 15]}
                head={head}
                rows={rows}
                showAmount={3}
                classNames={tableClasses}
                striped
              />
            </div>
            )
          : (variant === 'nocrit' ? <CriteriumSkeleton width='60%' /> : <Translate content='accessibility_contrast_ok' />)
      }
    </>
  );

  return <Criterium criterium={criterium}>{content}</Criterium>;
};

AccessibilityContrast.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          snippet: PropTypes.string,
          nodeLabel: PropTypes.string,
          fgColor: PropTypes.string,
          bgColor: PropTypes.string,
          contrastRatio: PropTypes.number,
          expectedContrastRatio: PropTypes.number
        })
      )
    })
  })
};

export default React.memo(AccessibilityContrast);
